/**
 * Factory for the manual line-link ``onConnect`` callback (#1120).
 *
 * Extracted from ``MatchGraph.tsx`` so the bail-toast contract added in
 * #1120 can be pinned in a Vitest unit test without mounting
 * ReactFlow. Mirrors the #1087 pattern for ``runManualLinkWithToast``:
 * pure logic in a sibling module, MatchGraph wires the React-side
 * concerns (``useCallback``, ``useToast``, ``setOptimisticEdges``).
 *
 * The factory replaces two pre-#1120 silent bails with toasts:
 *
 * 1. ``!normalised`` — connection handles aren't both line-spots, OR
 *    the line-side map doesn't cover them, OR they're on the same
 *    side. Pre-xyflow-validation reject (``isValidConnection`` should
 *    have caught it, defence-in-depth in case xyflow's contract
 *    changes or an edge case slips through).
 * 2. ``!deliverySrc || !settlementTgt`` — normalisation succeeded but
 *    the connection node ids are missing. Even more defensive (every
 *    normalised handle SHOULD have a card), but a silent bail there
 *    has the same UX failure mode.
 *
 * Both branches now surface ``INVALID_DROP_TOAST_MESSAGE`` so the
 * operator never sees a drag end without feedback.
 */
import type { Connection, Edge, OnConnectEnd } from '@xyflow/react';
import type { Dispatch, SetStateAction } from 'react';

import { runManualLinkWithToast } from './lineLinkErrorToast';
import {
  classifyLineConnection,
  isChromeHandle,
  isLineSpotHandle,
  isValidLineConnection,
  normaliseConnectionForLink,
  type ConnectionLike,
  type LineConnectionVerdict,
  type LineSideMap,
} from './lineLinkValidation';

/**
 * What one endpoint of a rejected drag actually was (HOF-90 Part 2).
 *
 * Deliberately a classification, not the handle id: the id carries an
 * ``event_id``, so shipping it would put document identifiers into a log
 * index for no query it enables. ``line`` / ``chrome`` is the pair that
 * separates "picked the right documents, wrong spot" from "dropped on
 * nothing linkable" — which is the whole reason this event exists.
 */
export type LineLinkEndpointKind = 'line' | 'chrome' | 'unknown';

/**
 * One rejected manual-link attempt, as the telemetry endpoint takes it
 * (``backend/src/schemas/telemetry.py::LineLinkRejectionTelemetryEvent``).
 */
export interface LineLinkRejection {
  verdict: LineConnectionVerdict;
  /** ``connect_end`` is the real canvas path; ``connect`` is the
   *  defence-in-depth backstop, so a non-zero count there means React Flow's
   *  ``isValidConnection`` short-circuit stopped holding. */
  stage: 'connect' | 'connect_end';
  source_kind: LineLinkEndpointKind;
  target_kind: LineLinkEndpointKind;
  /** ``null`` when the two endpoints do not agree on one group — which is
   *  itself the signal (a cross-group drop, or a card the maps don't cover). */
  group_id: string | null;
}

export type ReportLineLinkRejection = (rejection: LineLinkRejection) => void;

function endpointKind(handleId: string | null | undefined): LineLinkEndpointKind {
  if (isLineSpotHandle(handleId)) return 'line';
  if (isChromeHandle(handleId)) return 'chrome';
  return 'unknown';
}

/**
 * Resolve the match group a rejected drag was aimed at, or ``null``.
 *
 * A line handle resolves through ``lineSideMap``; a chrome handle carries no
 * line id, so it resolves through its CARD in ``cardSideMap``. When both
 * endpoints resolve and disagree, the answer is ``null`` rather than an
 * arbitrary pick — a cross-group drop has no single group, and naming one
 * would make the ELK row lie about which tile the operator was working.
 */
function rejectionGroupId(
  connection: ConnectionLike,
  lineSideMap: LineSideMap | undefined,
  cardSideMap: LineSideMap | undefined,
): string | null {
  const resolve = (handleId: string | null, nodeId: string | null): string | null => {
    if (isLineSpotHandle(handleId)) {
      return lineSideMap?.get(handleId as string)?.groupId ?? null;
    }
    return (nodeId ? cardSideMap?.get(nodeId)?.groupId : null) ?? null;
  };
  const a = resolve(connection.sourceHandle, connection.source);
  const b = resolve(connection.targetHandle, connection.target);
  if (a && b) return a === b ? a : null;
  return a ?? b;
}

/** Build the telemetry payload for a rejected connection. Exported so the
 *  MatchGraph wiring and the tests classify identically. */
export function describeRejection(
  connection: ConnectionLike,
  verdict: LineConnectionVerdict,
  stage: LineLinkRejection['stage'],
  lineSideMap: LineSideMap | undefined,
  cardSideMap: LineSideMap | undefined,
): LineLinkRejection {
  return {
    verdict,
    stage,
    source_kind: endpointKind(connection.sourceHandle),
    target_kind: endpointKind(connection.targetHandle),
    group_id: rejectionGroupId(connection, lineSideMap, cardSideMap),
  };
}

/**
 * Operator-visible copy for the pre-mutation reject branches, keyed by
 * the classified reason (#1391 11a). Each message states WHY the link
 * was rejected so a valid LS↔GS drag is accepted and a rejection no
 * longer reads as a "komische Warnung" (Nicole's feedback). The
 * pre-#1391 single generic string is kept as the ``valid``/fallback
 * entry for the (shouldn't-happen) normalisation-failed backstop.
 */
export const INVALID_DROP_TOAST_MESSAGES: Record<LineConnectionVerdict, string> = {
  valid: 'Verknüpfung nicht möglich – bitte erneut versuchen.',
  non_line:
    'Verknüpfung nicht möglich: Bitte eine Lieferschein-Warenzeile mit einer Gutschrift-Warenzeile verbinden (nicht den Belegkopf, die Referenz oder die Bestellung).',
  // #3237 — the operator dragged from the Lieferschein card's header
  // towards the Gutschrift card ("Lieferschein mit Gutschrift
  // verbinden"). Both endpoints are card chrome, but the two cards ARE
  // an LS/GS pair in one match group, so the link she wants is
  // expressible — just from the Waren-line spots, not the header. The
  // ``non_line`` copy above lists the three things not to touch and
  // never names the thing to touch; this one leads with the instruction.
  chrome_same_group:
    'Verknüpfung nicht möglich: Bitte die Warenzeilen verbinden — vom Punkt neben einer Lieferschein-Warenzeile zum Punkt neben der passenden Gutschrift-Warenzeile ziehen. Belegkopf, Referenz und Bestellung sind keine Verbindungspunkte.',
  same_side:
    'Verknüpfung nicht möglich: Eine Lieferscheinzeile muss mit einer Gutschriftzeile verbunden werden – nicht zwei Zeilen derselben Seite.',
  cross_group:
    'Verknüpfung nicht möglich: Diese Zeilen gehören zu verschiedenen Belegen / Match-Gruppen und können nicht verbunden werden.',
  unknown_endpoint:
    'Verknüpfung nicht möglich: Eine der Zeilen ist nicht (mehr) verfügbar – bitte Seite aktualisieren und erneut versuchen.',
};

/**
 * Resolve the operator-visible invalid-drop copy for a connection by
 * classifying its reject reason. A ``valid`` connection that still
 * reaches a reject branch (normalisation backstop) gets the generic
 * copy. Exported so tests assert against the classifier, not literals.
 */
export function invalidDropMessage(
  connection: ConnectionLike,
  lineSideMap: LineSideMap,
  /** #3237 — ``nodeId → {side, groupId}`` for the rendered LS/GS cards.
   *  Unlocks the actionable ``chrome_same_group`` copy; without it a
   *  header-to-header drop collapses onto the blunt ``non_line`` text. */
  cardSideMap?: LineSideMap,
): string {
  return INVALID_DROP_TOAST_MESSAGES[
    classifyLineConnection(connection, lineSideMap, cardSideMap)
  ];
}

/**
 * @deprecated Pre-#1391 single generic message. Retained as the
 * fallback string for the rare path where a connection classified
 * ``valid`` still fails normalisation (defence-in-depth). New code
 * should call {@link invalidDropMessage} for reason-specific copy.
 */
export const INVALID_DROP_TOAST_MESSAGE = INVALID_DROP_TOAST_MESSAGES.valid;

export interface BuildHandleConnectArgs {
  /** Storybook / standalone renders pass ``undefined`` to keep the
   *  no-interaction behaviour the canvas had before #1051 (the
   *  ``onConnect`` prop is conditionally wired in MatchGraph so this
   *  factory is the inner contract). */
  onManualLink:
    | ((args: {
        delivery_line_id: string;
        settlement_line_id: string;
      }) => Promise<unknown>)
    | undefined;
  lineSideMap: LineSideMap;
  /** #3237 — ``nodeId → {side, groupId}`` for the rendered LS/GS cards,
   *  so the defence-in-depth toasts below use the same reason-specific
   *  copy ``onConnectEnd`` does. */
  cardSideMap?: LineSideMap;
  showToast: (message: string, tone: 'error') => void;
  setOptimisticEdges: Dispatch<SetStateAction<Edge[]>>;
  /** HOF-90 Part 2 — called once per rejected drag so the reject leaves the
   *  browser. Injected rather than imported so this module stays pure logic,
   *  the same reason ``showToast`` is. Optional: storybook and the older
   *  tests keep their contract, and a missing reporter degrades to today's
   *  toast-only behaviour rather than throwing on the operator's drag. */
  reportRejection?: ReportLineLinkRejection;
}

export function buildHandleConnect(
  args: BuildHandleConnectArgs,
): (connection: Connection) => void {
  const {
    onManualLink,
    lineSideMap,
    cardSideMap,
    showToast,
    setOptimisticEdges,
    reportRejection,
  } = args;
  /** Toast + report, in one place, so a future reject branch cannot pick up
   *  the toast and silently skip the telemetry. */
  const reject = (connection: ConnectionLike): void => {
    const verdict = classifyLineConnection(connection, lineSideMap, cardSideMap);
    showToast(INVALID_DROP_TOAST_MESSAGES[verdict], 'error');
    reportRejection?.(
      describeRejection(connection, verdict, 'connect', lineSideMap, cardSideMap),
    );
  };
  return (connection: Connection) => {
    if (!onManualLink) return;
    // Codex round-1 P2 on PR #1133: ``normaliseConnectionForLink``
    // alone doesn't enforce the cross-group guard (different
    // ``groupId`` on each side), so a cross-group connection that
    // somehow bypasses xyflow's ``isValidConnection`` prop would
    // produce an optimistic edge + a ``onManualLink`` call instead
    // of a toast. Mirror the full validation contract here as
    // defence-in-depth so every silent reject becomes operator-
    // visible.
    if (!isValidLineConnection(connection, lineSideMap)) {
      reject(connection);
      return;
    }
    const normalised = normaliseConnectionForLink(connection, lineSideMap);
    if (!normalised) {
      reject(connection);
      return;
    }
    const optimisticId = `e_${normalised.delivery_line_id}_${normalised.settlement_line_id}`;
    // Look up the actual card node ids from the connection so the
    // optimistic edge anchors on the correct handles. The pre-existing
    // line-edge wiring keys handles by lineId, so we reuse the same
    // shape and just need to know which side each endpoint belongs to.
    const deliverySrc =
      connection.sourceHandle === normalised.delivery_line_id
        ? connection.source
        : connection.target;
    const settlementTgt =
      connection.targetHandle === normalised.settlement_line_id
        ? connection.target
        : connection.source;
    if (!deliverySrc || !settlementTgt) {
      reject(connection);
      return;
    }
    const optimisticEdge: Edge = {
      id: optimisticId,
      source: deliverySrc,
      sourceHandle: normalised.delivery_line_id,
      target: settlementTgt,
      targetHandle: normalised.settlement_line_id,
      type: 'straight',
      style: { stroke: '#10b981', strokeWidth: 1.25, opacity: 0.6 },
    };
    setOptimisticEdges((prev) => [
      ...prev.filter((e) => e.id !== optimisticId),
      optimisticEdge,
    ]);
    // #1087 — toast on rejection (instead of leaking as an unhandled
    // promise rejection), then always clear the optimistic edge.
    void runManualLinkWithToast({
      call: () => onManualLink(normalised),
      showToast,
      clearOptimisticEdge: () =>
        setOptimisticEdges((prev) => prev.filter((e) => e.id !== optimisticId)),
    });
  };
}

export interface BuildHandleConnectEndArgs {
  /** Same gate as ``buildHandleConnect``: standalone / storybook renders
   *  pass ``undefined`` so the canvas stays non-interactive. */
  onManualLink: BuildHandleConnectArgs['onManualLink'];
  showToast: (message: string, tone: 'error') => void;
  /** #1391 11a — when provided, the rejected-drop toast is reason-
   *  specific (built from the drop's ``fromHandle``/``toHandle`` ids via
   *  ``classifyLineConnection``). Omitted in older call sites / tests →
   *  falls back to the generic ``INVALID_DROP_TOAST_MESSAGE``. */
  lineSideMap?: LineSideMap;
  /** #3237 — ``nodeId → {side, groupId}`` for the rendered LS/GS cards.
   *  A header-to-header drop lands on two CHROME handles, which carry no
   *  line id, so the line map alone can only say ``non_line``. With the
   *  card map wired the factory can tell "you dropped on the right pair
   *  of documents, just the wrong spot" from "you dropped on nothing
   *  linkable" and surface the actionable copy for the first. */
  cardSideMap?: LineSideMap;
  /** HOF-90 Part 2 — see ``BuildHandleConnectArgs.reportRejection``. This is
   *  the handler that fires on the REAL canvas path, so it is where the
   *  fleet's operator-friction signal actually comes from. */
  reportRejection?: ReportLineLinkRejection;
}

/**
 * Factory for the manual line-link ``onConnectEnd`` callback (#1142).
 *
 * #1120 put the invalid-drop toast inside ``onConnect``
 * (``buildHandleConnect`` above), but React Flow evaluates the
 * ``isValidConnection`` prop FIRST and short-circuits invalid
 * connections, so ``onConnect`` never fires for them — the toast was
 * unreachable in the real RF path and an operator dropping on a
 * non-line, same-side, or cross-group handle got the original #1120
 * silent rejection, not a toast.
 *
 * ``onConnectEnd`` fires for EVERY drop attempt regardless of validity,
 * so the toast is surfaced here when the drop landed on a handle
 * (``toHandle`` set) that the validator rejected (``isValid === false``).
 * A release on empty canvas (``toHandle === null``) is a cancelled drag,
 * not an invalid link — no toast. A valid drop (``isValid === true``)
 * is owned by ``onConnect``, which paints the optimistic edge and
 * surfaces its own backend-error toast — so this handler stays silent
 * there to avoid a double toast.
 *
 * The defence-in-depth toast inside ``buildHandleConnect`` stays as a
 * backstop for the (shouldn't-happen) case where an invalid connection
 * slips past ``isValidConnection`` and reaches ``onConnect``.
 */
export function buildHandleConnectEnd(
  args: BuildHandleConnectEndArgs,
): OnConnectEnd {
  const { onManualLink, showToast, lineSideMap, cardSideMap, reportRejection } = args;
  return (_event, connectionState) => {
    if (!onManualLink) return;
    if (connectionState.isValid === false && connectionState.toHandle != null) {
      // #1391 11a — reason-specific copy when the side map is wired.
      // ``fromHandle``/``toHandle`` carry the dragged + dropped-on
      // handle ids; classify them so the operator sees WHY the drop was
      // rejected (same-side / cross-group / non-line) instead of one
      // generic "Anschluss muss …" string.
      const connection: ConnectionLike = {
        source: connectionState.fromNode?.id ?? null,
        target: connectionState.toNode?.id ?? null,
        sourceHandle: connectionState.fromHandle?.id ?? null,
        targetHandle: connectionState.toHandle?.id ?? null,
      };
      let message = INVALID_DROP_TOAST_MESSAGE;
      // HOF-90 Part 2 — the verdict is computed once and used for BOTH the
      // copy and the telemetry, so the row in ELK always names the reason the
      // operator was actually shown. Without ``lineSideMap`` the classifier
      // cannot do better than the generic fallback, and reporting a verdict
      // the operator was never given would be worse than reporting none.
      if (lineSideMap) {
        const verdict = classifyLineConnection(connection, lineSideMap, cardSideMap);
        message = INVALID_DROP_TOAST_MESSAGES[verdict];
        reportRejection?.(
          describeRejection(connection, verdict, 'connect_end', lineSideMap, cardSideMap),
        );
      }
      showToast(message, 'error');
    }
  };
}
